import React, { useState } from 'react';
import {
  Dialog,
  IconButton,
  Button,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface GenerateBlogGuidanceModalProps {
  open: boolean;
  onClose: () => void;
  blueprint: any | null;
  onGenerate: (blueprintId: string, suggestion?: string) => void;
  isGenerating?: boolean;
}

export const GenerateBlogGuidanceModal: React.FC<GenerateBlogGuidanceModalProps> = ({
  open,
  onClose,
  blueprint,
  onGenerate,
  isGenerating = false
}) => {
  const [suggestion, setSuggestion] = useState('');

  if (!blueprint) return null;

  const handleGenerate = () => {
    onGenerate(blueprint._id || blueprint.id, suggestion.trim() || undefined);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' }
      }}
    >
      <div className="p-6 relative">
        <div className="absolute top-4 right-4">
          <IconButton size="small" onClick={onClose} sx={{ color: '#667085' }}>
            <CloseIcon />
          </IconButton>
        </div>

        <h3 className="text-xl font-bold text-[#1D2939] m-0 mb-1">
          Generate Blog from Blueprint
        </h3>
        <p className="text-xs text-[#667085] mb-4">
          Orbit AI will generate a complete, SEO-optimised blog draft ready for review.
        </p>

        {/* Blueprint Summary Box */}
        <div className="p-3.5 bg-[#F9FAFB] rounded-xl border border-[#EAECF0] mb-4 space-y-2">
          <p className="font-semibold text-sm text-[#1D2939] m-0">
            {blueprint.title || blueprint.topic}
          </p>
          {blueprint.synopsis && (
            <p className="text-xs text-[#475467] leading-relaxed m-0">
              {blueprint.synopsis}
            </p>
          )}
          {blueprint.primary_keywords && blueprint.primary_keywords.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {blueprint.primary_keywords.map((kw: string, idx: number) => (
                <span
                  key={idx}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#ECFDF3] text-[#1A927B]"
                >
                  {kw}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Optional guidance */}
        <div className="mb-5">
          <label className="block text-xs font-semibold text-[#344054] mb-1.5">
            Any custom instructions or angle?{' '}
            <span className="text-[#667085] font-normal">(Optional)</span>
          </label>
          <textarea
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            rows={3}
            placeholder="e.g., Focus on step-by-step instructions, conversational tone, include a checklist header..."
            className="w-full text-xs text-[#1D2939] border border-[#D0D5DD] rounded-lg p-2.5 focus:outline-none focus:border-[#0F0A3C]"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating}
            sx={{
              backgroundColor: '#0F0A3C',
              color: '#FFFFFF',
              textTransform: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '13px',
              '&:hover': { backgroundColor: '#201861' }
            }}
          >
            {isGenerating ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              'Generate Blog'
            )}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default GenerateBlogGuidanceModal;
